import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { SectionLabel, RefreshBtn } from './shared';

// ── Database stats ────────────────────────────────────────────────────────────

function DatabaseStatsSection({ theme }) {
  const [infra, setInfra]       = useState(null);
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get('/api/admin/infrastructure'),
      axios.get('/api/admin/platform-stats'),
    ])
      .then(([i, p]) => { setInfra(i.data); setPlatform(p.data); })
      .catch(() => toast.error('Failed to load database stats'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const cacheHit      = platform?.db.cacheHitRate;
  const totalDbConns  = infra?.dbConnections?.reduce((s, r) => s + r.count, 0) ?? 0;
  const activeDbConns = infra?.dbConnections?.find(r => r.state === 'active')?.count ?? 0;
  const cacheColor    = cacheHit == null ? '#6c757d' : cacheHit >= 95 ? '#22c55e' : cacheHit >= 85 ? '#f59e0b' : '#ef4444';

  const cell = { padding: '10px 14px', borderBottom: `1px solid ${theme.border}`, fontSize: 12, color: theme.text, verticalAlign: 'middle' };
  const hCell = { ...cell, fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', background: theme.surfaceAlt, borderBottom: `2px solid ${theme.border}` };

  const statCard = (label, value, sub, color) => (
    <div style={{ background: theme.surfaceAlt, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || theme.text, lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme} action={<RefreshBtn onClick={load} loading={loading} theme={theme} />}>
        Database Stats
      </SectionLabel>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
        {statCard('DB size',     infra ? infra.dbSize.pretty : '—')}
        {statCard('Connections', infra ? String(totalDbConns) : '—', infra ? `${activeDbConns} active` : null)}
        {statCard('Pool',        infra ? String(infra.pool.total) : '—', infra ? `${infra.pool.idle} idle · ${infra.pool.waiting} queued` : null)}
        {statCard('Cache hit',   cacheHit != null ? `${cacheHit}%` : '—', null, cacheColor)}
      </div>

      {infra?.tables?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Table sizes</div>
          <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
              <thead>
                <tr>
                  <th style={hCell}>Table</th>
                  <th style={{ ...hCell, textAlign: 'right' }}>Rows (est.)</th>
                  <th style={{ ...hCell, textAlign: 'right' }}>Size</th>
                </tr>
              </thead>
              <tbody>
                {infra.tables.map((t, i) => (
                  <tr key={t.table_name} onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...cell, fontFamily: 'monospace', borderBottom: i < infra.tables.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{t.table_name}</td>
                    <td style={{ ...cell, textAlign: 'right', color: theme.textMuted, borderBottom: i < infra.tables.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{Number(t.row_count).toLocaleString()}</td>
                    <td style={{ ...cell, textAlign: 'right', color: theme.textMuted, fontFamily: 'monospace', borderBottom: i < infra.tables.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{t.total_size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {platform?.db.autovacuum?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dead tuples</div>
          <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
              <thead>
                <tr>
                  {['Table', 'Dead tuples', 'Last autovacuum'].map(h => (
                    <th key={h} style={{ ...hCell, textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {platform.db.autovacuum.map((t, i) => (
                  <tr key={t.table_name} onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...cell, fontFamily: 'monospace', borderBottom: i < platform.db.autovacuum.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{t.table_name}</td>
                    <td style={{ ...cell, color: Number(t.n_dead_tup) > 1000 ? '#f59e0b' : theme.textMuted, borderBottom: i < platform.db.autovacuum.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{Number(t.n_dead_tup).toLocaleString()}</td>
                    <td style={{ ...cell, fontSize: 11, color: theme.textMuted, borderBottom: i < platform.db.autovacuum.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{t.last_autovacuum ? new Date(t.last_autovacuum).toLocaleString('en-GB') : 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Backup log ────────────────────────────────────────────────────────────────

function BackupLogSection({ theme }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/admin/backup-log')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load backup log'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const { logs = [], summary = {} } = data ?? {};
  const lastSuccessHours = summary.last_success_at
    ? (Date.now() - new Date(summary.last_success_at)) / 3600000
    : null;
  const isStale = lastSuccessHours == null || lastSuccessHours > 25;
  const lastSizeMb = summary.last_size_bytes ? (summary.last_size_bytes / 1048576).toFixed(1) : null;

  const cell  = { padding: '10px 14px', fontSize: 12, color: theme.text, verticalAlign: 'middle' };
  const hCell = { ...cell, fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', background: theme.surfaceAlt, borderBottom: `2px solid ${theme.border}` };

  const formatBytes = b => b ? `${(b / 1048576).toFixed(1)} MB` : '—';
  const formatMs    = ms => ms ? (ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`) : '—';
  const formatTs    = ts => ts ? new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme} action={<RefreshBtn onClick={load} loading={loading} theme={theme} />}>
        DB Backups
      </SectionLabel>

      {isStale && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 14, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', fontSize: 13, color: '#ef4444' }}>
          <AlertTriangle size={15} />
          {lastSuccessHours == null ? 'No successful backup on record.' : `Last successful backup was ${Math.floor(lastSuccessHours)}h ago — over the 25h threshold.`}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Last backup',    value: summary.last_success_at ? formatTs(summary.last_success_at) : 'Never', small: true },
          { label: 'Last size',      value: lastSizeMb ? `${lastSizeMb} MB` : '—' },
          { label: 'Success (30d)',  value: summary.success_count ?? '—' },
          { label: 'Failures (30d)', value: summary.failure_count ?? '—', color: summary.failure_count > 0 ? '#ef4444' : undefined },
        ].map(c => (
          <div key={c.label} style={{ background: theme.surfaceAlt, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: c.small ? 13 : 22, fontWeight: 700, color: c.color || theme.text, lineHeight: 1.2 }}>{c.value}</div>
          </div>
        ))}
      </div>

      {logs.length === 0 ? (
        <div style={{ fontSize: 13, color: theme.textMuted }}>No backup runs recorded yet. First backup runs on next hourly check.</div>
      ) : (
        <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
            <thead>
              <tr>
                <th style={hCell}>Date</th>
                <th style={{ ...hCell, textAlign: 'right' }}>Size</th>
                <th style={{ ...hCell, textAlign: 'right' }}>Duration</th>
                <th style={{ ...hCell, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id} onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ ...cell, borderBottom: i < logs.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                    <div>{formatTs(log.created_at)}</div>
                    {log.filename && <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: 'monospace', marginTop: 2 }}>{log.filename}</div>}
                  </td>
                  <td style={{ ...cell, textAlign: 'right', color: theme.textMuted, borderBottom: i < logs.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{formatBytes(log.size_bytes)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: theme.textMuted, borderBottom: i < logs.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{formatMs(log.duration_ms)}</td>
                  <td style={{ ...cell, textAlign: 'center', borderBottom: i < logs.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                    {log.ok
                      ? <CheckCircle size={15} color="#22c55e" />
                      : <span title={log.error_message}><XCircle size={15} color="#ef4444" /></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export default function DatabaseTab({ theme }) {
  return (
    <>
      <DatabaseStatsSection theme={theme} />
      <BackupLogSection theme={theme} />
    </>
  );
}
